<template>
  <div>
    <sidebar></sidebar>

    <div :style="{ 'margin-left': sidebarwidth }">

      <!-- ── TOPBAR───── -->
      <div class="w-full bg-white grid grid-cols-2 gap-5 py-3 px-2 shadow-lg">
        <!-- Search — server-side via ?search= -->
        <div>
          <input
            type="text"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
            placeholder="Search Asset ID, Description, Category, Location..."
            v-model="searchQuery"
          />
        </div>

        <div class="flex gap-3 justify-end items-center text-sm flex-wrap">
          <button
            class="px-3 py-2 rounded-md text-sm"
            :class="filterState === 'all' ? 'bg-green-500 text-white' : 'bg-gray-200 text-black'"
            @click="setFilter('all')"
          >All</button>
          <button
            class="px-3 py-2 rounded-md text-sm"
            :class="filterState === 'printed' ? 'bg-green-500 text-white' : 'bg-gray-200 text-black'"
            @click="setFilter('printed')"
          >Printed</button>
          <button
            class="px-3 py-2 rounded-md text-sm"
            :class="filterState === 'not_printed' ? 'bg-green-500 text-white' : 'bg-gray-200 text-black'"
            @click="setFilter('not_printed')"
          >Not Printed</button>

          <div class="text-gray-600">Total:</div>
          <div class="font-bold border-r-2 pr-4">{{ totalAssets }}</div>

          <div class="flex items-center gap-2">
            <!-- Print Selected — any logged-in user (view_product) -->
            <button
              @click="printSelected"
              :disabled="noneSelected"
              class="px-3 py-2 rounded-md text-white font-semibold text-sm transition-colors"
              :class="noneSelected ? 'bg-gray-400 cursor-not-allowed' : 'bg-black hover:bg-orange-600 cursor-pointer'"
            >Print Selected</button>

            <!-- Add Asset — requires add_product permission -->
            <button
              v-if="can('add_product')"
              @click="openAddModal"
              class="px-3 py-2 rounded-md bg-green-600 text-white font-semibold text-sm hover:bg-green-700"
            >+ Add Asset</button>

            <!-- Upload Excel — requires add_product permission -->
            <button
              v-if="can('add_product')"
              @click="showUploadPopup = true"
              class="px-3 py-2 rounded-md bg-black text-white font-semibold text-sm hover:bg-blue-600"
            >Upload</button>

            <!-- Export — any logged-in user -->
            <button
              @click="exportToExcel"
              :disabled="exporting"
              class="px-3 py-2 rounded-md text-white font-semibold text-sm transition-colors disabled:opacity-60"
              :class="exporting ? 'bg-gray-400 cursor-not-allowed' : 'bg-black hover:bg-green-500'"
            >
              {{ exporting ? 'Exporting...' : selectedItems.length > 0 ? `Export (${selectedItems.length})` : 'Export All' }}
            </button>
          </div>
        </div>
      </div>
      <!-- ── END TOPBAR ──────── -->

      <!-- ── MAIN CONTENT ────── -->
      <div class="px-5">

        <!-- Pagination info row -->
        <div class="flex justify-between items-center py-2">
          <p class="font-semibold text-[18px]">Asset Information</p>
          <div class="flex items-center gap-1 text-sm">
            <span class="text-gray-500 mr-3">{{ totalAssets }} records</span>

            <!-- Previous -->
            <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-2 py-1 rounded text-sm transition-colors border"
              :class="currentPage === 1 ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:bg-gray-100 cursor-pointer'"
            >‹</button>

            <!-- Page numbers with smart windowing -->
            <template v-for="p in visiblePages" :key="p">
              <!-- Ellipsis -->
              <span v-if="p === '...'" class="px-2 py-1 text-gray-400 text-sm select-none">…</span>
              <!-- Page button -->
              <button
                v-else
                @click="goToPage(p)"
                class="min-w-[32px] px-2 py-1 rounded text-sm transition-colors border"
                :class="p === currentPage
                  ? 'bg-gray-900 text-white border-gray-900 font-semibold cursor-default'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-100 cursor-pointer'"
              >{{ p }}</button>
            </template>

            <!-- Next -->
            <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage === totalPages || totalPages === 0"
              class="px-2 py-1 rounded text-sm transition-colors border"
              :class="(currentPage === totalPages || totalPages === 0) ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:bg-gray-100 cursor-pointer'"
            >›</button>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="flex justify-center items-center py-20">
          <div class="text-gray-500 text-sm">Loading assets...</div>
        </div>

        <!-- Table -->
        <div v-else class="pt-2 w-full">
          <div class="relative overflow-x-auto">
            <table class="w-full text-sm text-left text-black">
              <thead class="text-xs text-white uppercase bg-black">
                <tr>
                  <th class="py-3 px-3"></th>
                  <th class="py-3 px-3">Asset ID</th>
                  <th class="py-3 px-4">Description</th>
                  <th class="py-3 px-3">Acquisition Date</th>
                  <th class="py-3 px-3">Category</th>
                  <th class="py-3 px-3">Location</th>
                  <th class="py-3 px-3">Group</th>
                  <th class="py-3 px-3">Life</th>
                  <th class="py-3 px-3">Book Value</th>
                  <th class="py-3 px-3">Net Value</th>
                  <th class="py-3 px-3">Remarks</th>
                  <th class="py-3 px-3">Upload Date</th>
                  <th class="py-3 px-3">Last Print Date</th>
                  <th class="py-3 px-2">Printed</th>
                  <th class="py-3 px-2">Action</th>
                </tr>
              </thead>
              <tbody>
                <!-- Empty state -->
                <tr v-if="displayedData.length === 0">
                  <td colspan="15" class="py-10 text-center text-gray-400">
                    No assets found.
                  </td>
                </tr>

                <tr
                  v-for="data in displayedData"
                  :key="data.id"
                  class="odd:bg-white even:bg-gray-50 border-b"
                >
                  <!-- Checkbox — printed assets remain selectable for export -->
                  <td class="py-4 px-3">
                    <input
                      type="checkbox"
                      :value="data.asset_id"
                      v-model="selectedItems"
                      @change="onCheckboxChange(data)"
                      class="cursor-pointer"
                    />
                  </td>

                  <!-- Correct backend field names -->
                  <td class="py-4 px-3 font-mono text-xs">{{ data.asset_id }}</td>
                  <td class="py-4 px-4">{{ data.description }}</td>
                  <td class="py-4 px-3">{{ data.acquisition_date }}</td>
                  <td class="py-4 px-3">{{ data.category }}</td>
                  <td class="py-4 px-3">{{ data.location }}</td>
                  <td class="py-4 px-3">{{ data.group }}</td>
                  <td class="py-4 px-3">{{ data.life }}</td>
                  <td class="py-4 px-3">{{ data.book_value }}</td>
                  <td class="py-4 px-3">{{ data.net_value }}</td>
                  <td class="py-4 px-3">
                    {{ data.remarks === 'nan' || !data.remarks ? '' : data.remarks }}
                  </td>
                  <td class="py-4 px-3 text-xs">
                    {{ dayjs(data.created_at).format('YYYY-MM-DD HH:mm') }}
                  </td>
                  <td class="py-4 px-3 text-xs">
                    {{ dayjs(data.updated_at).format('YYYY-MM-DD HH:mm') }}
                  </td>
                  <td class="py-4 px-2">
                    <span
                      class="px-2 py-1 rounded text-xs font-semibold"
                      :class="data.printed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'"
                    >{{ data.printed ? 'Yes' : 'No' }}</span>
                  </td>

                  <!-- Action buttons — permission gated -->
                  <td class="py-4 px-2">
                    <div class="flex gap-2 items-center">

                      <!-- Print with size dropdown -->
                      <div class="relative" @mouseleave="activePrintMenu = null">
                        <button
                          @click="activePrintMenu = activePrintMenu === data.id ? null : data.id"
                          class="text-xs px-2 py-1 rounded bg-gray-800 text-white hover:bg-orange-600 flex items-center gap-1"
                        >
                          Print
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                          </svg>
                        </button>
                        <!-- Size dropdown -->
                        <div
                          v-if="activePrintMenu === data.id"
                          class="absolute z-50 right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                        >
                          <div class="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b">
                            Label Size
                          </div>
                          <button
                            @click="handlePrint(data, 'medium'); activePrintMenu = null"
                            class="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left"
                          >
                            <span class="w-5 h-5 border-2 border-gray-400 rounded flex items-center justify-center text-[9px] font-bold text-gray-500">F</span>
                            <div>
                              <div class="font-medium">Full</div>
                              <div class="text-[10px] text-gray-400">70 × 100 mm</div>
                            </div>
                          </button>
                          <button
                            @click="handlePrint(data, 'small'); activePrintMenu = null"
                            class="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left border-t"
                          >
                            <span class="w-5 h-5 border-2 border-gray-400 rounded flex items-center justify-center text-[9px] font-bold text-gray-500">S</span>
                            <div>
                              <div class="font-medium">Small</div>
                              <div class="text-[10px] text-gray-400">50 × 70 mm</div>
                            </div>
                          </button>
                        </div>
                      </div>

                      <!-- Edit — requires change_product -->
                      <button
                        v-if="can('change_product')"
                        @click="openEditModal(data)"
                        class="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                      >Edit</button>

                      <!-- Delete — requires delete_product -->
                      <button
                        v-if="can('delete_product')"
                        @click="confirmDelete(data)"
                        class="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                      >Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Reset checkbox -->
        <div v-if="showResetButton" class="mt-4 flex justify-end">
          <button
            @click="resetSelectedItems"
            class="bg-red-500 text-white py-2 px-4 rounded text-sm"
          >Reset Selection</button>
        </div>
      </div>
      <!-- ── END MAIN CONTENT ─ -->
    </div>

    <!-- ══════════════ -->
    <!-- UPLOAD EXCEL MODAL-->
    <!-- ═══════════════ -->
    <div v-if="showUploadPopup" class="popupContainer" @click.self="!uploading && (showUploadPopup = false)">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div class="px-6 py-5 border-b">
          <h3 class="font-bold text-lg">Upload Excel File</h3>
          <p class="text-sm text-gray-500 mt-1">Supported format: .xlsx</p>
        </div>
        <div class="px-6 py-5">
          <!-- File picker — hidden while uploading -->
          <input
            v-if="!uploading"
            type="file"
            accept=".xls,.xlsx"
            @change="handleFileChange"
            class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
          />

          <!-- Progress bar — shown while uploading -->
          <div v-if="uploading" class="mt-2">
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium text-gray-700">Uploading file...</span>
              <span class="text-sm font-bold text-blue-600">{{ uploadProgress }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                class="h-4 rounded-full transition-all duration-300 ease-out"
                :class="uploadProgress < 100 ? 'bg-blue-500' : 'bg-green-500'"
                :style="{ width: uploadProgress + '%' }"
              ></div>
            </div>
            <p class="text-xs text-gray-400 mt-2 text-center">
              {{ uploadProgress < 100 ? 'Sending data to server...' : 'Processing records...' }}
            </p>
          </div>

          <!-- Status message after done -->
          <p v-if="uploadStatus && !uploading" class="mt-3 text-sm font-medium"
            :class="uploadSuccess ? 'text-green-600' : 'text-red-600'">
            {{ uploadStatus }}
          </p>
        </div>
        <div class="px-6 py-4 border-t flex justify-end gap-3">
          <button
            @click="showUploadPopup = false; uploadStatus = ''; uploadFile_ref = null; uploadProgress = 0"
            :disabled="uploading"
            class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
          >Cancel</button>
          <button
            :disabled="!uploadFile_ref || uploading"
            @click="uploadFile"
            class="px-4 py-2 rounded text-sm text-white font-semibold transition-colors"
            :class="(!uploadFile_ref || uploading) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'"
          >{{ uploading ? `${uploadProgress}%` : 'Upload' }}</button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════ -->
    <!-- ADD / EDIT ASSET MODAL -->
    <!-- ══════════════════════ -->
    <div v-if="showAssetModal" class="popupContainer" @click.self="closeAssetModal">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div class="px-6 py-5 border-b sticky top-0 bg-white z-10">
          <h3 class="font-bold text-lg">{{ isEditMode ? 'Edit Asset' : 'Add New Asset' }}</h3>
        </div>
        <div class="px-6 py-5 grid grid-cols-2 gap-4">
          <!-- Asset ID — only editable on create -->
          <div class="col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Asset ID <span class="text-red-500">*</span></label>
            <input
              v-model="assetForm.asset_id"
              :disabled="isEditMode"
              type="text"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500 disabled:bg-gray-100"
              placeholder="e.g. AST-00001"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input v-model="assetForm.description" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Acquisition Date</label>
            <input v-model="assetForm.acquisition_date" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" placeholder="e.g. 2024-01-15" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <input v-model="assetForm.category" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input v-model="assetForm.location" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Cost Center</label>
            <input v-model="assetForm.cost_center" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Group</label>
            <input v-model="assetForm.group" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Account Set</label>
            <input v-model="assetForm.account_set" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Responsible Person</label>
            <input v-model="assetForm.responsible_person" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Life</label>
            <input v-model="assetForm.life" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Book Value</label>
            <input v-model="assetForm.book_value" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">YTD Depreciation</label>
            <input v-model="assetForm.ytd_depreciation" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Accumulated Depreciation</label>
            <input v-model="assetForm.accumulated_depreciation" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Net Value</label>
            <input v-model="assetForm.net_value" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div class="col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea v-model="assetForm.remarks" rows="2" class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"></textarea>
          </div>
        </div>

        <div class="px-6 py-4 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
          <button @click="closeAssetModal" class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            @click="saveAsset"
            :disabled="savingAsset"
            class="px-4 py-2 rounded text-sm text-white font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
          >{{ savingAsset ? 'Saving...' : (isEditMode ? 'Update Asset' : 'Create Asset') }}</button>
        </div>
      </div>
    </div>

    <!-- ═════════════════════════ -->
    <!-- DELETE CONFIRMATION MODAL -->
    <!-- ═════════════════════════ -->
    <div v-if="showDeleteModal" class="popupContainer">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 class="font-bold text-lg mb-2">Delete Asset</h3>
        <p class="text-sm text-gray-600 mb-1">
          Are you sure you want to delete asset:
        </p>
        <p class="font-mono font-bold text-red-600 mb-5">{{ deleteTarget?.asset_id }}</p>
        <p class="text-xs text-gray-400 mb-5">This action cannot be undone.</p>
        <div class="flex justify-end gap-3">
          <button @click="showDeleteModal = false" class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            @click="deleteAsset"
            :disabled="deletingAsset"
            class="px-4 py-2 rounded text-sm text-white font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-60"
          >{{ deletingAsset ? 'Deleting...' : 'Yes, Delete' }}</button>
        </div>
      </div>
    </div>

    <!-- ════════════════════-->
    <!-- TOAST NOTIFICATIONS  -->
    <!-- ═══════════════════ -->
    <NotificationGroup group="home">
      <div class="fixed bottom-5 right-5 z-50 flex flex-col gap-2 pointer-events-none">
        <div class="w-full max-w-sm">
          <Notification
            v-slot="{ notifications }"
            enter="transform ease-out duration-300 transition"
            enter-from="translate-y-2 opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transition ease-in duration-300"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div
              v-for="n in notifications"
              :key="n.id"
              class="flex w-full max-w-sm mt-2 overflow-hidden bg-white rounded-lg shadow-lg border-l-4"
              :class="n.type === 'error' ? 'border-red-500' : 'border-green-500'"
            >
              <div class="px-4 py-3">
                <p class="font-semibold text-sm text-gray-800">{{ n.title }}</p>
                <p class="text-xs text-gray-500 mt-0.5">{{ n.text }}</p>
              </div>
            </div>
          </Notification>
        </div>
      </div>
    </NotificationGroup>

  </div>
</template>

<script setup>
import sidebar from "@/components/sidebar/Sidebar.vue";
import { ref, computed, onMounted, watch } from "vue";
import api from "../../boot/axios.js";
import * as XLSX from "xlsx";
import { sidebarwidth } from "@/store/index.js";
import QRCode from "qrcode";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import { notify } from "notiwind";
import { can } from "@/auth/permissions";

// ─── PERMISSION HELPER ──────
// Shared permission helper keeps route/sidebar/page checks consistent.

// ─── STATE ─────────────────
const productlist   = ref([]);       // current page data from server
const totalAssets   = ref(0);        // total count from server
const totalPages    = ref(1);
const currentPage   = ref(1);
const pageSize      = ref(15);       // matches backend default
const searchQuery   = ref("");
const filterState   = ref("all");    // 'all' | 'printed' | 'not_printed'
const loading       = ref(false);
const selectedItems = ref([]);
const showResetButton = ref(false);

// Upload
const showUploadPopup = ref(false);
const uploadFile_ref  = ref(null);
const uploadStatus    = ref("");
const uploadSuccess   = ref(false);
const uploading       = ref(false);
const uploadProgress  = ref(0);

// Cross-page selected items map: asset_id → full item object
const selectedItemsMap = ref({});

// Add / Edit modal
const showAssetModal = ref(false);
const isEditMode     = ref(false);
const savingAsset    = ref(false);
const editTargetId   = ref(null);
const assetForm      = ref(emptyForm());

// Delete modal
const showDeleteModal = ref(false);
const deleteTarget    = ref(null);
const deletingAsset   = ref(false);

function emptyForm() {
  return {
    asset_id: "",
    description: "",
    acquisition_date: "",
    category: "",
    location: "",
    cost_center: "",
    group: "",
    account_set: "",
    responsible_person: "",
    life: "",
    book_value: "0",
    ytd_depreciation: "0",
    accumulated_depreciation: "0",
    net_value: "0",
    remarks: "",
  };
}

const displayedData = computed(() => {
  if (filterState.value === "printed") {
    return productlist.value.filter((d) => d.printed);
  }
  if (filterState.value === "not_printed") {
    return productlist.value.filter((d) => !d.printed);
  }
  return productlist.value;
});

const noneSelected = computed(() => selectedItems.value.length === 0);

// Active print dropdown row id
const activePrintMenu = ref(null);

// ─── FETCH ASSETS (server-side pagination + search)
const fetchAssets = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      page_size: pageSize.value,
    };
    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim();
    }
    const res = await api.get("api/v1/productlist/", { params });
    if (res.data && res.data.results !== undefined) {
      // Paginated response shape:
      // { status, total, page, page_size, total_pages, results: [...] }
      productlist.value = res.data.results;
      totalAssets.value = res.data.total;
      totalPages.value  = res.data.total_pages || 1;
    } else {
      // Fallback: non-paginated (shouldn't happen but safe)
      productlist.value = Array.isArray(res.data) ? res.data : [];
      totalAssets.value = productlist.value.length;
      totalPages.value  = 1;
    }
  } catch (err) {
    toast("error", "Error", "Failed to load assets.");
  } finally {
    loading.value = false;
  }
};

// ─── SEARCH DEBOUNCE ──────
let searchTimer = null;
watch(searchQuery, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchAssets();
  }, 400);
});

// ─── PAGINATION ────────────
const goToPage = (p) => {
  if (p < 1 || p > totalPages.value || p === currentPage.value) return;
  currentPage.value = p;
  fetchAssets();
};

const prevPage = () => goToPage(currentPage.value - 1);
const nextPage = () => goToPage(currentPage.value + 1);

// Smart page window: always show first, last, current ±2, with ellipsis gaps
const visiblePages = computed(() => {
  const total = totalPages.value;
  const cur   = currentPage.value;
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = [];
  const addPage = (p) => { if (!pages.includes(p)) pages.push(p); };
  addPage(1);
  if (cur > 3) pages.push('...');
  for (let i = Math.max(2, cur - 2); i <= Math.min(total - 1, cur + 2); i++) addPage(i);
  if (cur < total - 2) pages.push('...');
  addPage(total);
  return pages;
});

// ─── FILTER (local on current page) ────────
const setFilter = (state) => {
  filterState.value = state;
  selectedItems.value = [];
  showResetButton.value = false;
};

// ─── PRINT SIZE CONFIG ──────────────────────
const PRINT_SIZES = {
  medium: { w: 70,  h: 100, qrX: 8,  qrY: 22, qrSize: 54, titleSize: 9,  descSize: 7,  idSize: 7,  titleY: 9,  descY: 16, idY: 80  },
  small:  { w: 50,  h: 70,  qrX: 5,  qrY: 16, qrSize: 40, titleSize: 7,  descSize: 6,  idSize: 6,  titleY: 7,  descY: 12, idY: 60  },
};

// ─── PRINT SINGLE ───────────────────────────
const handlePrint = async (data, size = 'small') => {
  try {
    const cfg = PRINT_SIZES[size];
    const doc = new jsPDF({ unit: "mm", format: [cfg.w, cfg.h] });
    const qrDataURI = await QRCode.toDataURL(data.asset_id, { width: 512, margin: 1 });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(cfg.titleSize);
    doc.text(`Asset ID: ${data.asset_id}`, cfg.w / 2, cfg.titleY, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(cfg.descSize);
    const desc = data.description ? String(data.description).substring(0, 60) : "";
    doc.text(desc, cfg.w / 2, cfg.descY, { align: "center", maxWidth: cfg.w - 10 });

    doc.addImage(qrDataURI, "PNG", cfg.qrX, cfg.qrY, cfg.qrSize, cfg.qrSize);

    doc.setFontSize(cfg.idSize);
    doc.text(data.asset_id, cfg.w / 2, cfg.idY, { align: "center" });

    doc.save(`${data.asset_id}_${size}.pdf`);

    await api.patch(`api/v1/productlist/${data.id}/`, { printed: true });
    data.printed = true;
    toast("success", "Printed", `${data.asset_id} — ${size} label saved.`);
  } catch (error) {
    toast("error", "Error", "Failed to print asset.");
  }
};

// ─── PRINT SELECTED ────────────────
const printSelected = async () => {
  try {
    const selectedData = displayedData.value.filter((item) =>
      selectedItems.value.includes(item.asset_id)
    );
    if (!selectedData.length) return;

    await Promise.all(
      selectedData.map((d) =>
        api.patch(`api/v1/productlist/${d.id}/`, { printed: true })
      )
    );
    selectedData.forEach((d) => { d.printed = true; });

    await generatePDF(selectedData, 'small');

    selectedItems.value = [];
    showResetButton.value = false;
    toast("success", "Printed", `${selectedData.length} asset(s) printed.`);
  } catch (error) {
    toast("error", "Error", "Failed to print selected assets.");
  }
};

const resetSelectedItems = () => {
  selectedItems.value    = [];
  selectedItemsMap.value = {};
  showResetButton.value  = false;
};

// ─── GENERATE PDF (multi) ───────────────────────
async function generatePDF(items, size = 'small') {
  const cfg = PRINT_SIZES[size];
  const doc = new jsPDF({ unit: "mm", format: [cfg.w, cfg.h] });

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const qrDataURI = await QRCode.toDataURL(item.asset_id, { width: 512, margin: 1 });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(cfg.titleSize);
    doc.text(`Asset ID: ${item.asset_id}`, cfg.w / 2, cfg.titleY, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(cfg.descSize);
    const desc = item.description ? String(item.description).substring(0, 60) : "";
    doc.text(desc, cfg.w / 2, cfg.descY, { align: "center", maxWidth: cfg.w - 10 });

    doc.addImage(qrDataURI, "PNG", cfg.qrX, cfg.qrY, cfg.qrSize, cfg.qrSize);

    doc.setFontSize(cfg.idSize);
    doc.text(item.asset_id, cfg.w / 2, cfg.idY, { align: "center" });

    if (i < items.length - 1) doc.addPage();
  }
  doc.save("assets_print.pdf");
}

// ─── CHECKBOX HANDLER ──────────────────────────────
// Keeps a cross-page map of selected items' full data
const onCheckboxChange = (item) => {
  if (selectedItems.value.includes(item.asset_id)) {
    selectedItemsMap.value[item.asset_id] = item;
  } else {
    delete selectedItemsMap.value[item.asset_id];
  }
  showResetButton.value = selectedItems.value.length > 0;
};

// ─── EXPORT TO EXCEL ───────────────────────────────
const exporting = ref(false);

const exportToExcel = async () => {
  exporting.value = true;

  let rows = [];
  const hasSelection = selectedItems.value.length > 0;

  if (hasSelection) {
    // Export only selected items — already in memory from cross-page map
    rows = Object.values(selectedItemsMap.value);
    toast("success", "Exporting", `Exporting ${rows.length} selected asset(s).`);
  } else {
    // Export full list — fetch all records from server
    toast("success", "Exporting", "Fetching all records, please wait...");
    try {
      const res = await api.get("api/v1/productlist/", {
        params: {
          page: 1,
          page_size: 99999,
          ...(searchQuery.value.trim() ? { search: searchQuery.value.trim() } : {}),
        },
      });
      rows = res.data.results || [];
    } catch {
      toast("error", "Export Failed", "Could not fetch all records.");
      exporting.value = false;
      return;
    }
  }

  const headerRow = [
    "Asset ID", "Description", "Acquisition Date", "Category",
    "Location", "Cost Center", "Group", "Account Set",
    "Responsible Person", "Life", "Book Value", "YTD Depreciation",
    "Accumulated Depreciation", "Net Value", "Remarks",
    "Upload Date", "Last Print Date", "Printed",
  ];

  const dataRows = rows.map((item) => [
    item.asset_id,
    item.description,
    item.acquisition_date,
    item.category,
    item.location,
    item.cost_center,
    item.group,
    item.account_set,
    item.responsible_person,
    item.life,
    item.book_value,
    item.ytd_depreciation,
    item.accumulated_depreciation,
    item.net_value,
    item.remarks === "nan" || !item.remarks ? "" : item.remarks,
    item.created_at ? dayjs(item.created_at).format("YYYY-MM-DD HH:mm:ss") : "",
    item.updated_at ? dayjs(item.updated_at).format("YYYY-MM-DD HH:mm:ss") : "",
    item.printed ? "Yes" : "No",
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
  const workbook  = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Assets");
  const filename  = hasSelection
    ? `assets_selected_${selectedItems.value.length}.xlsx`
    : `assets_all_${dayjs().format("YYYY-MM-DD")}.xlsx`;
  XLSX.writeFile(workbook, filename);
  exporting.value = false;
};

// ─── UPLOAD EXCEL ───────────────
const handleFileChange = (event) => {
  uploadFile_ref.value = event.target.files[0];
  uploadStatus.value   = "";
  uploadProgress.value = 0;
};

const uploadFile = async () => {
  if (!uploadFile_ref.value) return;
  uploading.value      = true;
  uploadStatus.value   = "";
  uploadSuccess.value  = false;
  uploadProgress.value = 0;

  try {
    const formData = new FormData();
    formData.append("file", uploadFile_ref.value);

    const res = await api.post(
      "api/v1/productUpload/",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        // Real-time upload progress via XMLHttpRequest
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            uploadProgress.value = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          }
        },
      }
    );

    if (res.data.status === true) {
      uploadSuccess.value  = true;
      uploadProgress.value = 100;
      uploadStatus.value   = `✓ ${res.data.message} (${res.data.total_uploaded ?? ""} records uploaded)`;
      toast("success", "Upload Successful", res.data.message);
      setTimeout(() => {
        showUploadPopup.value  = false;
        uploadFile_ref.value   = null;
        uploadStatus.value     = "";
        uploadProgress.value   = 0;
        currentPage.value      = 1;
        fetchAssets();
      }, 1800);
    } else {
      uploadSuccess.value  = false;
      uploadProgress.value = 0;
      uploadStatus.value   = `✗ ${res.data.message}`;
      toast("error", "Upload Failed", res.data.message);
    }
  } catch (err) {
    uploadSuccess.value  = false;
    uploadProgress.value = 0;
    uploadStatus.value   = "Upload failed. Please check the file and try again.";
    toast("error", "Upload Error", "Could not upload file.");
  } finally {
    uploading.value = false;
  }
};

// ─── ADD ASSET ───────────
const openAddModal = () => {
  isEditMode.value    = false;
  editTargetId.value  = null;
  assetForm.value     = emptyForm();
  showAssetModal.value = true;
};

// ─── EDIT ASSET ───────────
const openEditModal = (data) => {
  isEditMode.value   = true;
  editTargetId.value = data.id;
  assetForm.value    = {
    asset_id:                 data.asset_id,
    description:              data.description               || "",
    acquisition_date:         data.acquisition_date          || "",
    category:                 data.category                  || "",
    location:                 data.location                  || "",
    cost_center:              data.cost_center               || "",
    group:                    data.group                     || "",
    account_set:              data.account_set               || "",
    responsible_person:       data.responsible_person        || "",
    life:                     data.life                      || "",
    book_value:               data.book_value                || "0",
    ytd_depreciation:         data.ytd_depreciation          || "0",
    accumulated_depreciation: data.accumulated_depreciation  || "0",
    net_value:                data.net_value                 || "0",
    remarks:                  data.remarks === "nan" ? "" : (data.remarks || ""),
  };
  showAssetModal.value = true;
};

const closeAssetModal = () => {
  showAssetModal.value = false;
  savingAsset.value    = false;
};

// ─── SAVE ASSET (create or update) ────────────
const saveAsset = async () => {
  if (!assetForm.value.asset_id.trim()) {
    toast("error", "Validation", "Asset ID is required.");
    return;
  }
  savingAsset.value = true;
  try {
    if (isEditMode.value) {
      // PATCH — partial update
      const res = await api.patch(
        `api/v1/productlist/${editTargetId.value}/`,
        assetForm.value
      );
      if (res.data.status === true) {
        toast("success", "Updated", `Asset ${assetForm.value.asset_id} updated.`);
        closeAssetModal();
        fetchAssets();
      } else {
        toast("error", "Error", res.data.message || "Update failed.");
      }
    } else {
      // POST — create new
      const res = await api.post("api/v1/productlist/", assetForm.value);
      if (res.status === 201 && res.data.status === true) {
        toast("success", "Created", `Asset ${assetForm.value.asset_id} created.`);
        closeAssetModal();
        currentPage.value = 1;
        fetchAssets();
      } else {
        toast("error", "Error", res.data?.message || "Create failed.");
      }
    }
  } catch (err) {
    const msg = err?.response?.data
      ? Object.values(err.response.data).flat().join(" ")
      : "Something went wrong.";
    toast("error", "Error", msg);
  } finally {
    savingAsset.value = false;
  }
};

// ─── DELETE ASSET ────────────
const confirmDelete = (data) => {
  deleteTarget.value  = data;
  showDeleteModal.value = true;
};

const deleteAsset = async () => {
  if (!deleteTarget.value) return;
  deletingAsset.value = true;
  try {
    const res = await api.delete(`api/v1/productlist/${deleteTarget.value.id}/`);
    if (res.status === 200 && res.data.status === true) {
      toast("success", "Deleted", res.data.message);
      showDeleteModal.value = false;
      deleteTarget.value    = null;
      // If we deleted the last item on this page, go back one page
      if (productlist.value.length === 1 && currentPage.value > 1) {
        currentPage.value--;
      }
      fetchAssets();
    } else {
      toast("error", "Error", res.data?.message || "Delete failed.");
    }
  } catch (err) {
    toast("error", "Error", "Could not delete asset.");
  } finally {
    deletingAsset.value = false;
  }
};

// ─── TOAST HELPER ──────────────────
const toast = (type, title, text) => {
  notify({ group: "home", type, title, text }, 3000);
};

// ─── INIT ──────────────────────────
onMounted(() => {
  fetchAssets();
});
</script>

<style scoped>
.popupContainer {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 50;
}
</style>